"use client";

import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@/lib/utils/validation/zod-react-hook-form-resolver";
import { MessageSquare, Lock, Eye, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Checkbox } from "@/components/ui/checkbox";
import { useCreateTaskNote, useUpdateTaskNote, type CreateTaskNote } from "@/lib/hooks/api/tasks";
import { CreateTaskNoteSchema } from "@/lib/schemas/task";

interface CreateTaskNoteFormProps {
  taskId: string;
  initialValues?: Partial<CreateTaskNote>;
  noteId?: string; // For editing existing notes
  onSuccess: () => void;
  onCancel: () => void;
}

export function CreateTaskNoteForm({
  taskId,
  initialValues,
  noteId,
  onSuccess,
  onCancel,
}: CreateTaskNoteFormProps) {
  const [isInternal, setIsInternal] = useState(initialValues?.is_internal || false);

  const createNoteMutation = useCreateTaskNote();
  const updateNoteMutation = useUpdateTaskNote();
  const isEditing = !!noteId;

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<CreateTaskNote>({
    resolver: zodResolver(CreateTaskNoteSchema as any),
    defaultValues: {
      task_id: taskId,
      content: initialValues?.content || "",
      note_type: initialValues?.note_type || "general",
      is_internal: initialValues?.is_internal || false,
    },
  });

  const noteType = watch("note_type");

  const onSubmit = async (data: CreateTaskNote) => {
    try {
      if (isEditing && noteId) {
        await updateNoteMutation.mutateAsync({
          id: noteId,
          content: data.content,
          note_type: data.note_type,
          is_internal: data.is_internal,
        });
      } else {
        await createNoteMutation.mutateAsync(data);
      }
      onSuccess();
    } catch (error) {
      console.error("Error saving note:", error);
    }
  };

  const getNoteTypeDescription = (type: string) => {
    switch (type) {
      case "general":
        return "General notes visible to all team members";
      case "internal":
        return "Internal notes for team discussion only";
      case "client":
        return "Notes that can be shared with clients";
      default:
        return "";
    }
  };

  const getNoteTypeIcon = (type: string) => {
    switch (type) {
      case "internal":
        return <Lock className="h-4 w-4" />;
      case "client":
        return <Eye className="h-4 w-4" />;
      case "general":
      default:
        return <MessageSquare className="h-4 w-4" />;
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Note Type Selection */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Note Type</Label>
        <RadioGroup
          value={noteType}
          onValueChange={(value: "general" | "internal" | "client") => setValue("note_type", value)}
          className="grid grid-cols-3 gap-4"
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="general" id="general" />
            <Label htmlFor="general" className="flex items-center gap-2 cursor-pointer">
              <MessageSquare className="h-4 w-4 text-gray-500" />
              <span className="text-sm">General</span>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="internal" id="internal" />
            <Label htmlFor="internal" className="flex items-center gap-2 cursor-pointer">
              <Lock className="h-4 w-4 text-red-500" />
              <span className="text-sm">Internal</span>
            </Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="client" id="client" />
            <Label htmlFor="client" className="flex items-center gap-2 cursor-pointer">
              <Eye className="h-4 w-4 text-blue-500" />
              <span className="text-sm">Client</span>
            </Label>
          </div>
        </RadioGroup>
        <p className="text-xs text-gray-500 flex items-center gap-2">
          {getNoteTypeIcon(noteType)}
          {getNoteTypeDescription(noteType)}
        </p>
      </div>

      {/* Internal Note Checkbox */}
      <div className="flex items-center space-x-2">
        <Checkbox
          id="is_internal"
          checked={isInternal}
          onCheckedChange={(checked) => {
            setIsInternal(!!checked);
            setValue("is_internal", !!checked);
          }}
        />
        <Label htmlFor="is_internal" className="flex items-center gap-2 cursor-pointer">
          <Lock className="h-4 w-4 text-red-500" />
          <span className="text-sm">Make this an internal note (team only)</span>
        </Label>
      </div>

      {/* Note Content */}
      <div className="space-y-2">
        <Label htmlFor="content" className="text-sm font-medium">
          Note Content
        </Label>
        <Textarea
          id="content"
          {...register("content")}
          placeholder="Enter your note here..."
          className="min-h-[120px] resize-none"
          rows={6}
        />
        {errors.content && <p className="text-sm text-red-500">{errors.content.message}</p>}
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
          Cancel
        </Button>
        <Button type="submit" disabled={isSubmitting} className="gap-2">
          {isSubmitting ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              {isEditing ? "Updating..." : "Adding..."}
            </>
          ) : (
            <>
              <MessageSquare className="h-4 w-4" />
              {isEditing ? "Update Note" : "Add Note"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
